import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { TrendingUp, BookOpen, Zap, Trophy, BarChart2, MessageSquare, ArrowDown } from 'lucide-react';

const TX = {
  fr: {
    badge: 'Disponible sur iOS & Android',
    title: 'Investissez intelligemment',
    titleBold: 'sans spéculation',
    subtitle: 'L\'app qui vous apprend à investir avec la tête, pas les émotions.',
    free: 'Téléchargement gratuit',
    features: [
      { icon: '🎓', text: 'Académie — 5 modules complets' },
      { icon: '💎', text: 'Pépites IA — 500+ actifs scannés' },
      { icon: '⚔️', text: 'AI Battle — 4 IAs en compétition' },
      { icon: '📊', text: 'Simulateur — $10k virtuels' },
      { icon: '🤖', text: 'AI Chat — conseiller personnel' },
      { icon: '📈', text: 'Portfolio — suivi en temps réel' },
    ],
    googlePlay: 'Google Play',
    appStore: 'App Store',
    available: 'Disponible sur',
    disclaimer: 'Simulation éducative uniquement — pas de conseil financier',
    web: 'Ou accédez via le web',
  },
  en: {
    badge: 'Available on iOS & Android',
    title: 'Invest intelligently',
    titleBold: 'without speculation',
    subtitle: 'The app that teaches you to invest with your head, not your emotions.',
    free: 'Free download',
    features: [
      { icon: '🎓', text: 'Academy — 5 complete modules' },
      { icon: '💎', text: 'AI Gems — 500+ assets scanned' },
      { icon: '⚔️', text: 'AI Battle — 4 AIs competing' },
      { icon: '📊', text: 'Simulator — $10k virtual' },
      { icon: '🤖', text: 'AI Chat — personal advisor' },
      { icon: '📈', text: 'Portfolio — real-time tracking' },
    ],
    googlePlay: 'Google Play',
    appStore: 'App Store',
    available: 'Available on',
    disclaimer: 'Educational simulation only — not financial advice',
    web: 'Or access via web',
  },
  es: {
    badge: 'Disponible en iOS y Android',
    title: 'Invierte inteligentemente',
    titleBold: 'sin especulación',
    subtitle: 'La app que te enseña a invertir con la cabeza, no las emociones.',
    free: 'Descarga gratuita',
    features: [
      { icon: '🎓', text: 'Academia — 5 módulos completos' },
      { icon: '💎', text: 'Gemas IA — 500+ activos analizados' },
      { icon: '⚔️', text: 'AI Battle — 4 IAs compitiendo' },
      { icon: '📊', text: 'Simulador — $10k virtuales' },
      { icon: '🤖', text: 'AI Chat — asesor personal' },
      { icon: '📈', text: 'Cartera — seguimiento en tiempo real' },
    ],
    googlePlay: 'Google Play',
    appStore: 'App Store',
    available: 'Disponible en',
    disclaimer: 'Solo simulación educativa — no es asesoramiento financiero',
    web: 'O accede vía web',
  },
};

export default function AppLanding() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tx = TX[language] || TX.fr;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col" style={{ maxWidth: '480px', margin: '0 auto' }}>

      {/* Hero */}
      <div className="flex-1 flex flex-col px-6 pt-10 pb-6">

        {/* Logo */}
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-[#ff6b35] rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-black" />
          </div>
          <span className="font-black text-white text-xl">Do Not Bull</span>
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#ff6b35]/15 border border-[#ff6b35]/30 rounded-full text-xs text-[#ff6b35] font-semibold mb-5 self-start">
          📱 {tx.badge}
        </div>

        {/* Headline */}
        <h1 className="text-4xl font-black text-white leading-tight mb-4">
          {tx.title}<br />
          <span className="text-[#ff6b35]">{tx.titleBold}</span>
        </h1>

        <p className="text-gray-400 text-base leading-relaxed mb-8">
          {tx.subtitle}
        </p>

        {/* App Store Buttons */}
        <div className="flex flex-col gap-3 mb-8">
          {/* App Store */}
          <a
            href="https://apps.apple.com/app/id6774533419"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 bg-white text-black px-5 py-4 rounded-2xl font-bold transition-all active:scale-95"
            style={{ boxShadow: '0 4px 20px rgba(255,255,255,0.1)' }}
          >
            <svg viewBox="0 0 814 1000" className="w-8 h-8 fill-current flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.8 0 263.6 0 127.3c0-150.1 97.8-248.4 250.2-248.4 69 0 127.4 36.2 179 81.9 13.4 11.5 27.5 23.4 42.5 35.6 44.6-37.3 100.3-108.7 179.5-108.7 3.2 0 6.4.3 9.6.5 2.2.1 4.4.3 6.5.4l.2.1z"/>
            </svg>
            <div>
              <div className="text-xs text-gray-600 font-normal leading-tight">{tx.available}</div>
              <div className="text-lg font-black leading-tight">{tx.appStore}</div>
            </div>
            <div className="ml-auto text-xs bg-black/10 px-2 py-1 rounded-full">iOS</div>
          </a>

          {/* Google Play */}
          <a
            href="https://play.google.com/store/apps/details?id=com.donotbull.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 bg-[#ff6b35] text-black px-5 py-4 rounded-2xl font-bold transition-all active:scale-95"
            style={{ boxShadow: '0 4px 20px rgba(255,107,53,0.3)' }}
          >
            <svg viewBox="0 0 24 24" className="w-8 h-8 fill-current flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-11.57L13.34 9l-10.16 14.76zm-.96-21.5C2.08 2.57 2 2.9 2 3.24v17.52c0 .34.08.67.22.96L13.06 12 2.22 2.26zM20.49 10.34l-2.76-1.57-3.67 3.36 3.67 3.36 2.8-1.59c.8-.45.8-1.01-.04-1.56zM4.17.24L16.77 11.8 13.34 15 4.17.24z" />
            </svg>
            <div>
              <div className="text-xs text-black/60 font-normal leading-tight">{tx.available}</div>
              <div className="text-lg font-black leading-tight">{tx.googlePlay}</div>
            </div>
            <div className="ml-auto text-xs bg-black/10 px-2 py-1 rounded-full">Android</div>
          </a>
        </div>

        {/* Free badge */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="h-px bg-gray-800 flex-1" />
          <span className="text-xs text-gray-500 px-3">✓ {tx.free}</span>
          <div className="h-px bg-gray-800 flex-1" />
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          {tx.features.map((f, i) => (
            <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-2xl p-4">
              <div className="text-2xl mb-2">{f.icon}</div>
              <div className="text-xs text-gray-300 font-semibold leading-snug">{f.text}</div>
            </div>
          ))}
        </div>

        {/* AI Battle preview */}
        <div className="bg-gradient-to-br from-purple-900/30 to-orange-900/20 border border-gray-800 rounded-2xl p-5 mb-8">
          <div className="text-xs text-yellow-400 font-bold mb-3 flex items-center gap-1">
            <Trophy className="w-3 h-3" /> LIVE
          </div>
          <div className="text-white font-black text-lg mb-3">⚔️ AI Portfolio Battle</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { name: 'NOVA', roi: '+7.77%', color: '#10A37F', avatar: '⚡' },
              { name: 'NEXUS', roi: '+7.43%', color: '#4285F4', avatar: '✦' },
              { name: 'ENTROPY', roi: '+6.52%', color: '#9333EA', avatar: '🌀' },
              { name: 'APEX', roi: '+4.05%', color: '#FF8C42', avatar: '🧠' },
            ].map(ai => (
              <div key={ai.name} className="bg-black/30 rounded-xl p-3 flex items-center gap-2">
                <span className="text-lg">{ai.avatar}</span>
                <div>
                  <div className="text-xs font-bold text-white">{ai.name}</div>
                  <div className="text-xs font-black" style={{ color: ai.color }}>{ai.roi}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Web link */}
        <button
          onClick={() => navigate('/')}
          className="text-center text-gray-600 text-xs underline mb-4"
        >
          {tx.web} → donotbull.com
        </button>

        {/* Disclaimer */}
        <p className="text-center text-xs text-gray-700 pb-4">
          ⚠️ {tx.disclaimer}
        </p>
      </div>
    </div>
  );
}
